import React, { useState, useCallback } from 'react';
import { X, Trash2 } from 'lucide-react';
import { useLazyCommand, useDomainEvent, useEvent } from '@soap/modules';

interface DeleteAssessmentResultsDialogProps {
    assessmentResultId: string;
    unitId: string;
    onConfirm: () => void;
    onClose: () => void;
}

const viewStates = {
    ready: 0,
    submitting: 1
};

function matchesAssessmentResultsDeletedEvent(
    event: Record<string, unknown>,
    assessmentResultId: string,
    unitId: string
): boolean {
    const eventResultId =
        (event.e270_AssessmentResultId as string | undefined) ??
        (event.E270_AssessmentResultId as string | undefined);
    const eventUnitId =
        (event.e270_UnitId as string | undefined) ?? (event.E270_UnitId as string | undefined);

    return eventResultId === assessmentResultId || eventUnitId === unitId;
}

export function DeleteAssessmentResultsDialog({ assessmentResultId, unitId, onConfirm, onClose }: DeleteAssessmentResultsDialogProps) {
    const [viewState, setViewState] = useState(viewStates.ready);
    const [sendDeleteResultsCommand] = useLazyCommand('C282v1_DeleteAssessmentResults');
    const triggerCloseContextMenu = useDomainEvent({
        eventName: "close-context-menu",
    });

    const handleClose = useCallback(() => {
        setViewState(viewStates.ready);
        onClose();
        triggerCloseContextMenu();
    }, [onClose, triggerCloseContextMenu]);

    useEvent({
        eventName: 'E270v1_AssessmentResultsDeleted',
        onEventReceived: (event: Record<string, unknown>) => {
            if (matchesAssessmentResultsDeletedEvent(event, assessmentResultId, unitId)) {
                handleClose();
            }
        }
    }, [assessmentResultId, unitId, handleClose]);

    const handleDelete = async () => {
        setViewState(viewStates.submitting);

        try {
            await sendDeleteResultsCommand({
                C282_AssessmentResultId: assessmentResultId
            });
            onConfirm();
            handleClose();
        } catch (error) {
            console.error('Error deleting assessment results:', error);
            setViewState(viewStates.ready);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Delete Assessment Results</h2>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-destructive-action/10 p-4 rounded-lg border border-destructive-action/30">
                            <p className="text-sm text-destructive-action-dark">
                                This action cannot be undone.
                            </p>
                            <p className="text-sm text-destructive-action-dark mt-2">
                                Are you sure you want to delete these assessment results?
                            </p>
                            <p className="text-sm text-destructive-action-dark mt-2">
                                The student will need to retake the assessment.
                            </p>
                        </div>

                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                            <h4 className="font-medium text-yellow-900 mb-2 flex items-center">
                                <Trash2 size={16} className="mr-2" />
                                What will be deleted:
                            </h4>
                            <ul className="ml-6 space-y-1 list-disc">
                                <li className="text-sm text-yellow-800">All assessment results and answers</li>
                                <li className="text-sm text-yellow-800">All progress made by the student</li>
                                <li className="text-sm text-yellow-800">The assessment will revert to unassigned state</li>
                            </ul>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-6">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                void handleDelete();
                            }}
                            className="px-6 py-2 bg-destructive-action hover:bg-destructive-action-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            disabled={viewState === viewStates.submitting}
                        >
                            <Trash2 size={16} className={`mr-2 ${viewState === viewStates.submitting ? 'animate-spin' : ''}`} />
                            {viewState === viewStates.submitting ? 'Deleting...' : 'Delete Results'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
